import { type AllProduct, type Variation } from '@hooks-api/types/dataModel/products'
import { type ProductCardBadgeType, type ProductCardEntry } from '../types/product-card-entry'

type ProductMeta = Pick<AllProduct, 'id' | 'title' | 'thumbnail' | 'last_gallery_image'>

interface BuildVariationEntryParams {
  product: ProductMeta
  variation: Variation
  badge?: ProductCardBadgeType
}

/**
 * 一張卡 = 一個 variation。卡片的 price 取 variation
 * key 以 variation_id 確保同 product 多 variation 各自唯一。
 *
 * subtitleChips 由 variation.name 以全形「｜」分隔，此分隔符為與 PM 約定的契約。
 */
export const buildVariationEntry = (params: BuildVariationEntryParams): ProductCardEntry => {
  const { product, variation, badge } = params
  const subtitleChips = variation.name.split('｜').map(part => part.trim()).filter(Boolean)
  return {
    key: `${product.id}_${variation.variation_id}`,
    productId: product.id,
    title: product.title,
    imageUrl: variation.parent_last_gallery_image || variation.thumbnail || product.thumbnail,
    price: variation.sale_price,
    subtitleChips,
    badge,
    variations: [variation],
  }
}

interface BuildProductCardEntryParams {
  product: ProductMeta
  /** 該 product 底下所有 eligible variations；卡片價格取 min(sale_price) */
  eligibleVariations: Variation[]
  badge?: ProductCardBadgeType
}

/**
 * 一張卡 = 一個 product（聚合多個 eligible variations）。卡片價格取 min。
 */
export const buildProductCardEntry = (params: BuildProductCardEntryParams): ProductCardEntry => {
  const { product, eligibleVariations, badge } = params
  const [firstVariation, ...restVariations] = eligibleVariations
  if (!firstVariation) {
    throw new Error('buildProductCardEntry requires at least one eligible variation')
  }
  const minPrice = restVariations.reduce(
    (min, variation) => Math.min(min, variation.sale_price),
    firstVariation.sale_price,
  )
  return {
    key: String(product.id),
    productId: product.id,
    title: product.title,
    imageUrl: product.last_gallery_image || product.thumbnail,
    price: minPrice,
    badge,
    variations: eligibleVariations,
  }
}
